import React from 'react'
import { cn } from '../../utils/cn'

interface LayoutProps {
  children: React.ReactNode
  sidebar?: React.ReactNode
  header?: React.ReactNode
  className?: string
}

export function Layout({ children, sidebar, header, className }: LayoutProps) {
  return (
    <div className={cn('flex h-screen bg-gray-50 dark:bg-gray-950', className)}>
      {sidebar && (
        <aside className="w-52 bg-white dark:bg-gray-900 border-r border-gray-200 dark:border-gray-700 flex flex-col">
          {sidebar}
        </aside>
      )}
      <div className="flex-1 flex flex-col overflow-hidden">
        {header && (
          <header className="h-16 bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between px-6">
            {header}
          </header>
        )}
        <main className="flex-1 overflow-auto p-6">
          {children}
        </main>
      </div>
    </div>
  )
}

interface NavItem {
  path: string
  label: string
  icon: React.ReactNode
  roles?: string[]
  requiresMission?: boolean
  requiresFunction?: string[]
  children?: NavItem[]
  badge?: string
  badgeVariant?: 'default' | 'success' | 'warning' | 'danger'
}

interface SidebarProps {
  items: NavItem[]
  activeItem?: string
  onItemClick?: (item: NavItem) => void
  className?: string
}

export function Sidebar({ items, activeItem, onItemClick, className }: SidebarProps) {
  return (
    <nav className={cn('flex-1 overflow-y-auto p-4 space-y-2', className)}>
      {items.map(item => (
        <NavItemComponent
          key={item.path}
          item={item}
          isActive={activeItem === item.path}
          onClick={onItemClick}
        />
      ))}
    </nav>
  )
}

function NavItemComponent({ item, isActive, onClick }: { item: NavItem; isActive: boolean; onClick?: (item: NavItem) => void }) {
  const hasChildren = item.children && item.children.length > 0

  if (hasChildren) {
    return (
      <div className="space-y-1">
        <div className="px-3 py-1.5 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
          {item.label}
        </div>
        <div className="space-y-0.5 ml-2">
          {item.children!.map(child => (
            <NavItemComponent
              key={child.path}
              item={child}
              isActive={false}
              onClick={onClick}
            />
          ))}
        </div>
      </div>
    )
  }

  return (
    <button
      onClick={() => onClick?.(item)}
      className={cn(
        'w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors',
        isActive
          ? 'bg-primary-50 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300'
          : 'text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800'
      )}
    >
      <span className="flex-shrink-0 w-5 h-5">{item.icon}</span>
      <span className="flex-1 truncate">{item.label}</span>
      {item.badge && (
        <span className="ml-auto px-2 py-0.5 text-xs font-medium rounded-full">
          {item.badge}
        </span>
      )}
    </button>
  )
}

interface PageHeaderProps {
  title?: string
  subtitle?: string
  action?: React.ReactNode
  breadcrumbs?: Array<{ label: string; path?: string }>
}

export function PageHeader({ title, subtitle, action, breadcrumbs }: PageHeaderProps) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
      <div>
        {breadcrumbs && breadcrumbs.length > 0 && (
          <nav className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400 mb-2">
            {breadcrumbs.map((crumb, i) => (
              <span key={i} className="flex items-center gap-2">
                {i > 0 && <span>/</span>}
                {crumb.path ? (
                  <a href={crumb.path} className="hover:text-gray-700 dark:hover:text-gray-200">
                    {crumb.label}
                  </a>
                ) : (
                  <span className="font-medium text-gray-900 dark:text-dark-text">{crumb.label}</span>
                )}
              </span>
            ))}
          </nav>
        )}
        {title && <h1 className="text-2xl font-bold text-gray-900 dark:text-dark-text">{title}</h1>}
        {subtitle && <p className="mt-1 text-gray-500 dark:text-gray-400">{subtitle}</p>}
      </div>
      {action && <div className="flex-shrink-0">{action}</div>}
    </div>
  )
}